import { Avatar, AvatarFallback, AvatarImage, Button, DataTable, type TanstackReactTable } from "@devhop/ui";
import { Eye } from "lucide-react";
import type { EmployeeProfileListItem } from "@/modules/hr/employee-profile/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { EmployeeStatusBadge } from "./EmployeeStatusBadge";

type Props = {
  data: EmployeeProfileListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onView: (row: EmployeeProfileListItem) => void;
};

export function EmployeeProfileTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onView,
}: Props) {
  const columns: TanstackReactTable.ColumnDef<EmployeeProfileListItem>[] = [
    {
      id: "employee",
      header: "ພະນັກງານ",
      size: 220,
      cell: ({ row }) => {
        const r = row.original;
        const initials = (r.userName ?? r.email)
          .split(" ")
          .map((w) => w[0])
          .join("")
          .toUpperCase()
          .slice(0, 2);
        return (
          <div className="flex items-center gap-3">
            <Avatar className="size-9 shrink-0">
              <AvatarImage src={r.image ?? undefined} />
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate font-medium text-sm">
                {r.nameEn ?? r.nameLao ?? r.userName ?? "—"}
              </div>
              <div className="truncate text-muted-foreground text-xs">
                {r.email}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "employeeCode",
      header: "ລະຫັດ",
      size: 90,
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.employeeCode ?? "—"}</span>
      ),
    },
    {
      id: "branch",
      header: "ສາຂາ",
      size: 80,
      cell: ({ row }) => (
        <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
          {row.original.branch ?? "—"}
        </span>
      ),
    },
    {
      id: "position",
      header: "ຕຳແໜ່ງ / ພະແນກ",
      size: 200,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="min-w-0">
            <div className="truncate text-sm">{r.positionTitle ?? "—"}</div>
            <div className="truncate text-muted-foreground text-xs">
              {r.departmentName ?? "—"}
            </div>
          </div>
        );
      },
    },
    {
      id: "status",
      header: "ສະຖານະ",
      size: 140,
      cell: ({ row }) => (
        <EmployeeStatusBadge
          status={row.original.status}
          probationEndDate={row.original.probationEndDate}
        />
      ),
    },
    {
      id: "probationEndDate",
      header: "ສິ້ນສຸດທົດລອງ",
      size: 130,
      cell: ({ row }) => {
        const d = row.original.probationEndDate;
        if (!d) return <span className="text-muted-foreground text-xs">—</span>;
        const days = Math.round(
          (new Date(d).getTime() - Date.now()) / 86_400_000,
        );
        const isWarning = days >= 0 && days <= 30;
        return (
          <div className={isWarning ? "text-destructive" : ""}>
            <div className="text-sm">{formatDateLocal(d)}</div>
            {days >= 0 && (
              <div className="text-xs opacity-70">
                {days === 0 ? "ວັນນີ້" : `ອີກ ${days} ວັນ`}
              </div>
            )}
            {days < 0 && (
              <div className="text-muted-foreground text-xs">
                ກາຍກຳນົດ {Math.abs(days)} ວັນ
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "startDate",
      header: "ວັນເລີ່ມງານ",
      size: 110,
      cell: ({ row }) => {
        const d = row.original.startDate;
        return d ? (
          <span className="text-sm">{formatDateLocal(d)}</span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      size: 60,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => onView(row.original)}
          title="ເບິ່ງລາຍລະອຽດ"
        >
          <Eye className="size-4" />
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      pagination={{
        offset,
        limit,
        totalCount,
        onChange: onPaginationChange,
      }}
    />
  );
}
